import { Alert, Badge, Button, Group, Stack, Text, Tooltip } from '@mantine/core';

import { RegistryOrderError } from '@/services/registry-order-error';
import {
  describeRegistryPrice,
  REGISTRY_ORDER_STATUS_META,
  type RegistryOrderStatus,
} from '@/types/registry-documents';
import { countryNameFor } from './orderDraft';
import type { OrderCompany } from './useOrderCompany';
import type { OrderDocuments } from './useOrderDocuments';

interface ConfirmStepProps {
  company: OrderCompany;
  documents: OrderDocuments;
  reportActivated: boolean | null;
  error: Error | null;
  submitting: boolean;
  onOrderAnyway: () => void;
}

/** Step 3 — what is ordered, what it costs, and when it is sent. */
export function ConfirmStep({
  company,
  documents,
  reportActivated,
  error,
  submitting,
  onOrderAnyway,
}: Readonly<ConfirmStepProps>) {
  return (
    <Stack gap="sm" mt="sm">
      <Summary company={company} documents={documents} reportActivated={reportActivated} />
      {error && (
        <SubmitErrorAlert
          error={error}
          submitting={submitting}
          reportActivated={reportActivated}
          onOrderAnyway={onOrderAnyway}
        />
      )}
    </Stack>
  );
}

function Summary({
  company,
  documents,
  reportActivated,
}: Readonly<{
  company: OrderCompany;
  documents: OrderDocuments;
  reportActivated: boolean | null;
}>) {
  const manual = company.mode === 'manual';
  const companyName = manual
    ? company.manual.values.companyName.trim()
    : (company.candidate?.name ?? '—');
  const { quoteCount, total } = documents;
  return (
    <Stack gap="xs" data-testid="registry-order-summary">
      <Group gap="xs" wrap="wrap">
        <Text size="sm" fw={600}>
          {companyName}
        </Text>
        <Text size="sm" c="dimmed">
          · {countryNameFor(company.country)}
        </Text>
        {manual && (
          <Tooltip label="Not in Armadillo's index — their research desk identifies it from the details in the order note">
            <Badge size="xs" variant="light" color="grape">
              by name
            </Badge>
          </Tooltip>
        )}
      </Group>
      <Stack gap={2}>
        {documents.chosen.map((product) => (
          <Group key={product.code} justify="space-between">
            <Text size="sm">{product.description ?? product.code}</Text>
            <Text size="sm">
              {describeRegistryPrice(product.price, product.currency, product.requiresQuote) ?? '—'}
            </Text>
          </Group>
        ))}
      </Stack>
      {total && (
        <Group justify="space-between">
          <Text size="sm" fw={600}>
            List price
          </Text>
          <Text size="sm" fw={600}>
            {describeRegistryPrice(total.amount, total.currency, false)}
          </Text>
        </Group>
      )}
      {quoteCount > 0 && (
        <Alert color="orange" variant="light">
          {quoteCount === 1 ? 'One document is' : `${quoteCount} documents are`} priced on quote:
          Armadillo emails the price and turnaround first, and nothing is fulfilled until it is
          accepted. The order carries no list price until then.
        </Alert>
      )}
      <Alert color="blue" variant="light">
        {whenSent(reportActivated)}
      </Alert>
    </Stack>
  );
}

/** When the order goes to Armadillo. `subject` names it: "The order" for
 * one, "Each order" for a batch. */
export function whenSent(reportActivated: boolean | null, subject = 'The order'): string {
  if (reportActivated === null) {
    return `${subject} is sent to Armadillo at once if the report has started; otherwise it is recorded on the report and sent when the report starts.`;
  }
  return reportActivated
    ? `${subject} is sent to Armadillo now. Documents arrive by email and appear on the report as they are delivered.`
    : `${subject} is recorded on the report and sent to Armadillo when the report starts. Until then it can be discarded.`;
}

function statusLabel(status: string | null): string | null {
  if (!status) {
    return null;
  }
  return REGISTRY_ORDER_STATUS_META[status as RegistryOrderStatus]?.label ?? status;
}

function referenceNote(error: RegistryOrderError): string {
  return error.reference ? ` Reference ${error.reference}.` : '';
}

/** The company a batch row's alert is about. Several rows can offer
 * "Order anyway" at once, so each button is named and identified by its
 * company; the single flow passes nothing and keeps the plain button. */
export interface OrderAnywaySubject {
  id: string;
  name: string;
}

/** Why an order was not placed, in the API's terms. Shared with the batch
 * confirm step, where it sits under the company it is about. */
export function SubmitErrorAlert({
  error,
  submitting,
  reportActivated,
  onOrderAnyway,
  subject,
}: Readonly<{
  error: Error;
  submitting: boolean;
  reportActivated: boolean | null;
  onOrderAnyway: () => void;
  subject?: OrderAnywaySubject;
}>) {
  if (!(error instanceof RegistryOrderError)) {
    return (
      <Alert color="red" title="Could not prepare the order">
        {error.message}
      </Alert>
    );
  }
  switch (error.kind) {
    case 'duplicate':
      return (
        <DuplicateAlert
          error={error}
          submitting={submitting}
          onOrderAnyway={onOrderAnyway}
          subject={subject}
        />
      );
    case 'outcome_unknown':
      return (
        <Alert color="orange" title={reportActivated ? 'Sent, but not confirmed' : 'Not confirmed'}>
          {reportActivated
            ? `${error.message}`
            : 'The request did not complete, and the order may still have been recorded.'}
          {referenceNote(error)} Check the report's orders before trying again — a second attempt
          for the same documents is refused while the first is open.
        </Alert>
      );
    case 'accepted_without_documents':
      return (
        <Alert color="red" title="Accepted by Armadillo without every document">
          {error.message}
          {referenceNote(error)}
        </Alert>
      );
    case 'not_sent':
    case 'refused':
      return (
        <Alert color="red" title="Recorded, but not placed">
          {error.message}
          {referenceNote(error)}
        </Alert>
      );
    default:
      return (
        <Alert color="red" title="Could not prepare the order">
          {error.message}
        </Alert>
      );
  }
}

function DuplicateAlert({
  error,
  submitting,
  onOrderAnyway,
  subject,
}: Readonly<{
  error: RegistryOrderError;
  submitting: boolean;
  onOrderAnyway: () => void;
  subject?: OrderAnywaySubject;
}>) {
  const label = statusLabel(error.existingStatus);
  const which = [error.reference, label?.toLowerCase()].filter(Boolean).join(', ');
  return (
    <Alert color="yellow" title="Already ordered on this report">
      <Stack gap="xs">
        <Text size="sm">
          An open order for these documents is already on the report{which ? ` (${which})` : ''}.
          {error.existingStatus === 'pending'
            ? ' That one was sent and not yet confirmed — it may be live at Armadillo; check with their research desk before ordering again.'
            : ' Order again only if it is a deliberate re-order.'}
        </Text>
        <Button
          size="xs"
          variant="light"
          color="yellow"
          onClick={onOrderAnyway}
          loading={submitting}
          aria-label={subject ? `Order anyway for ${subject.name}` : undefined}
          data-testid={subject ? `registry-order-anyway-${subject.id}` : 'registry-order-anyway'}
        >
          Order anyway
        </Button>
      </Stack>
    </Alert>
  );
}
